/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());
  // Ref para controlar tentativas de refresh_token simultâneas
  const isRefreshing = useRef(false);
  const refreshPromise = useRef(null);
  // Flag para indicar que o refresh_token falhou definitivamente
  const refreshFailed = useRef(false);

  const isPublicAccessPath = () => {
    const pathname = history.location.pathname || "";
    return pathname.startsWith("/form/");
  };

  const redirectToLogin = () => {
    if (history.location.pathname !== "/login" && !isPublicAccessPath()) {
      history.replace("/login");
    }
  };

  const clearAuthSession = (shouldRedirect = true) => {
    refreshFailed.current = true;
    isRefreshing.current = false;
    refreshPromise.current = null;
    localStorage.removeItem("token");
    api.defaults.headers.Authorization = undefined;
    setIsAuth(false);
    setUser({});
    setLoading(false);
    if (shouldRedirect) {
      redirectToLogin();
    }
  };

  // Interceptors do API (mantém como estava)
  if (!api.__authInterceptorsConfigured) {
    api.__authInterceptorsConfigured = true;

    api.interceptors.request.use(
    async (config) => {
      // ✅ CORREÇÃO: Verificar se config.url existe antes de acessar
      if (!config?.url) {
        return config;
      }
      
      const isRefreshToken = config?.url?.includes("/auth/refresh_token");

      // Rotas que não precisam de token (login, refresh_token, etc)
      const publicRoutes = ['/auth/login', '/auth/refresh_token', '/lead-forms/public'];
      const isPublicRoute = publicRoutes.some(route => config.url?.includes(route));

      if (!isPublicRoute && refreshFailed.current) {
        clearAuthSession(true);
        return Promise.reject(new Error("Sessao expirada"));
      }
      
      // Se é uma rota pública, não verificar refreshFailed
      if (isPublicRoute) {
        // Ainda pode ter token se estiver disponível, mas não é obrigatório
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const parsedToken = JSON.parse(token);
            config.headers["Authorization"] = `Bearer ${parsedToken}`;
          } catch (err) {
            // Ignorar erro de parse para rotas públicas
          }
        }
        return config;
      }

      // Não adicionar token se o refresh já falhou (exceto para rotas públicas)
      if (refreshFailed.current) {
        // Não adicionar token, a requisição vai falhar no backend
        return config;
      }
      
      // Se está fazendo refresh e a requisição não é o próprio refresh_token, aguardar
      if (isRefreshing.current && refreshPromise.current && config?.url && !config.url.includes("/auth/refresh_token")) {
        try {
          await refreshPromise.current;
          // Verificar se o refresh não falhou enquanto aguardava
          if (refreshFailed.current) {
            clearAuthSession(true);
            return Promise.reject(new Error("Sessao expirada"));
          }
        } catch (err) {
          clearAuthSession(true);
          return Promise.reject(new Error("Sessao expirada"));
        }
      }
      
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const parsedToken = JSON.parse(token);
          config.headers["Authorization"] = `Bearer ${parsedToken}`;
        } catch (err) {
          clearAuthSession(true);
          return Promise.reject(new Error("Sessao invalida"));
        }
      } else if (!isPublicRoute && history.location.pathname !== "/login") {
        clearAuthSession(true);
        return Promise.reject(new Error("Sessao ausente"));
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // ✅ CORREÇÃO: Verificar se error.config existe antes de usar
      const originalRequest = error?.config;
      
      // Se não tiver config, pode ser um erro de rede ou outro tipo de erro
      if (!originalRequest) {
        // ✅ CORREÇÃO: Não tentar acessar propriedades que não existem
        const errorMessage = error?.message || error?.toString() || 'Erro desconhecido';
        // ✅ CORREÇÃO: Criar um erro seguro para rejeitar
        const safeError = error?.response ? error : new Error(errorMessage);
        if (error?.response) {
          safeError.response = error.response;
        }
        return Promise.reject(safeError);
      }
      
      // Ignorar erros 401 de push-subscriptions - não devem causar logout
      if (originalRequest?.url?.includes("/push-subscriptions") && error?.response?.status === 401) {
        return Promise.reject(error); // Rejeitar sem fazer logout
      }
      
      // Ignorar erros 401 de login - são erros de credenciais, não de token
      if (originalRequest?.url?.includes("/auth/login") && error?.response?.status === 401) {
        return Promise.reject(error); // Rejeitar sem tentar refresh
      }

      // Integração com CRM usa erros 400/401 como falha de negócio, não como expiração de sessão
      if (originalRequest?.url?.includes("/crm-integration") && error?.response?.status === 401) {
        return Promise.reject(error);
      }

      // Ignorar erros 404 de disk-usage - não são críticos
      if (originalRequest?.url?.includes("/disk-usage") && error?.response?.status === 404) {
        return Promise.reject(error);
      }
      
      // Não tentar refresh se a requisição original já era um refresh_token
      if (originalRequest?.url?.includes("/auth/refresh_token")) {
        clearAuthSession(true);
        // Se o refresh_token falhou, marcar como falho e limpar tudo
        refreshFailed.current = true;
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        setUser({});
        isRefreshing.current = false;
        refreshPromise.current = null;
        // Redirecionar para login se não estiver já lá
        if (history.location.pathname !== "/login" && !isPublicAccessPath()) {
          history.push("/login");
        }
        return Promise.reject(error);
      }

      // Se o refresh já falhou anteriormente, rejeitar imediatamente sem tentar novamente
      if (refreshFailed.current) {
        if (error?.response?.status === 403 || error?.response?.status === 401) {
          clearAuthSession(true);
          return Promise.reject(error);
        }
      }

      // Se recebeu 403 e ainda não tentou refresh
      if (error?.response?.status === 403 && !originalRequest?._retry && !refreshFailed.current) {
        // Se já está tentando refresh, aguardar a promessa existente
        if (isRefreshing.current && refreshPromise.current) {
          try {
            await refreshPromise.current;
            // Verificar se o refresh não falhou enquanto aguardava
            if (refreshFailed.current) {
              return Promise.reject(error);
            }
            // Tentar novamente a requisição original com o novo token
            const token = localStorage.getItem("token");
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
              return api(originalRequest);
            }
          } catch (err) {
            // Se o refresh falhou, marcar como falho e rejeitar
            clearAuthSession(true);
            refreshFailed.current = true;
            return Promise.reject(error);
          }
        }

        // Se não está tentando refresh, iniciar uma nova tentativa
        if (!isRefreshing.current) {
          isRefreshing.current = true;
          originalRequest._retry = true;

          refreshPromise.current = (async () => {
            try {
              const token = localStorage.getItem("token");
              if (!token) {
                throw new Error("No token available");
              }

              const { data } = await api.post("/auth/refresh_token");
              if (data && data.token) {
                // Resetar a flag de falha se o refresh foi bem-sucedido
                refreshFailed.current = false;
                localStorage.setItem("token", JSON.stringify(data.token));
                api.defaults.headers.Authorization = `Bearer ${data.token}`;
                setIsAuth(true);
                if (data.user) {
                  setUser(data.user);
                }
                return data;
              }
              throw new Error("No token in response");
            } catch (refreshError) {
              clearAuthSession(true);
              // Se o refresh_token falhar, marcar como falho e limpar tudo
              refreshFailed.current = true;
              localStorage.removeItem("token");
              api.defaults.headers.Authorization = undefined;
              setIsAuth(false);
              setUser({});
              // ✅ CORREÇÃO: Criar um erro com estrutura compatível para evitar problemas no interceptor
              const errorToThrow = refreshError?.response 
                ? refreshError 
                : new Error(refreshError?.message || "Refresh token failed");
              // Preservar config se existir
              if (refreshError?.config) {
                errorToThrow.config = refreshError.config;
              } else {
                // ✅ CORREÇÃO: Se não tiver config, adicionar uma propriedade vazia para evitar erros
                errorToThrow.config = { url: undefined };
              }
              throw errorToThrow;
            } finally {
              isRefreshing.current = false;
              refreshPromise.current = null;
            }
          })();

          try {
            await refreshPromise.current;
            // Tentar novamente a requisição original com o novo token
            const token = localStorage.getItem("token");
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
              return api(originalRequest);
            }
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
      }

      // Se recebeu 401 e ainda não tentou refresh (e não é push-subscriptions)
      // Também verificar se não acabou de fazer login (aguardar 2 segundos após login)
      const justLoggedIn = Date.now() - (window.__lastLoginTime || 0) < 2000;
      if (error?.response?.status === 401 && !originalRequest?._retry && !refreshFailed.current && !originalRequest?.url?.includes("/push-subscriptions") && !justLoggedIn) {
        if (!localStorage.getItem("token")) {
          clearAuthSession(true);
          return Promise.reject(error);
        }
        
        // Se já está tentando refresh, aguardar a promessa existente
        if (isRefreshing.current && refreshPromise.current) {
          try {
            await refreshPromise.current;
            // Verificar se o refresh não falhou enquanto aguardava
            if (refreshFailed.current) {
              return Promise.reject(error);
            }
            // Tentar novamente a requisição original com o novo token
            const token = localStorage.getItem("token");
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
              originalRequest._retry = true;
              return api(originalRequest);
            }
          } catch (err) {
            return Promise.reject(error);
          }
        }

        // Se não está tentando refresh, iniciar uma nova tentativa
        if (!isRefreshing.current) {
          isRefreshing.current = true;
          originalRequest._retry = true;

          refreshPromise.current = (async () => {
            try {
              const token = localStorage.getItem("token");
              if (!token) {
                throw new Error("No token available");
              }

              const { data } = await api.post("/auth/refresh_token");
              if (data && data.token) {
                // Resetar a flag de falha se o refresh foi bem-sucedido
                refreshFailed.current = false;
                localStorage.setItem("token", JSON.stringify(data.token));
                api.defaults.headers.Authorization = `Bearer ${data.token}`;
                setIsAuth(true);
                if (data.user) {
                  setUser(data.user);
                }
                return data;
              }
              throw new Error("No token in response");
            } catch (refreshError) {
              clearAuthSession(true);
              // Se o refresh_token falhar, marcar como falho e limpar tudo
              refreshFailed.current = true;
              localStorage.removeItem("token");
              api.defaults.headers.Authorization = undefined;
              setIsAuth(false);
              setUser({});
              // ✅ CORREÇÃO: Criar um erro com estrutura compatível para evitar problemas no interceptor
              const errorToThrow = refreshError?.response 
                ? refreshError 
                : new Error(refreshError?.message || "Refresh token failed");
              // Preservar config se existir
              if (refreshError?.config) {
                errorToThrow.config = refreshError.config;
              } else {
                // ✅ CORREÇÃO: Se não tiver config, adicionar uma propriedade vazia para evitar erros
                errorToThrow.config = { url: undefined };
              }
              throw errorToThrow;
            } finally {
              isRefreshing.current = false;
              refreshPromise.current = null;
            }
          })();

          try {
            await refreshPromise.current;
            // Tentar novamente a requisição original com o novo token
            const token = localStorage.getItem("token");
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
              return api(originalRequest);
            }
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
      }
      
      // Se recebeu 401 e já tentou refresh ou refresh falhou, fazer logout
      if (error?.response?.status === 401 && (originalRequest?._retry || refreshFailed.current)) {
        clearAuthSession(true);
        refreshFailed.current = true;
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        setUser({});
        isRefreshing.current = false;
        refreshPromise.current = null;
      }
      
      return Promise.reject(error);
    }
  );

  // Effect para inicialização do token
  }

  useEffect(() => {
    // ✅ Verificar se o token está corrompido antes de usar
    let token = null;
    try {
      const rawToken = localStorage.getItem("token");
      if (rawToken) {
        // Verificar se o token JWT tem formato válido (3 partes separadas por ponto)
        const parts = rawToken.split('.');
        if (parts.length === 3 && parts.every(part => part.length > 0)) {
          token = rawToken;
        } else {
          console.warn("⚠️ Token com formato inválido detectado. Removendo...");
          localStorage.removeItem("token");
        }
      }
    } catch (e) {
      console.error("Erro ao verificar token:", e);
      localStorage.removeItem("token");
    }

    (async () => {
      if (token) {
        // Marcar que está fazendo refresh para evitar conflitos com interceptor
        if (!isRefreshing.current) {
          isRefreshing.current = true;
          refreshFailed.current = false; // Resetar flag de falha
          
          // Criar a promessa de refresh para que outras requisições possam aguardar
          refreshPromise.current = (async () => {
            try {
              // Verificar se há cookies (não podemos ler diretamente, mas podemos verificar se a requisição vai enviá-los)
              if (document.cookie) {
                const cookies = document.cookie.split(';');
                const jrtCookie = cookies.find(c => c.trim().startsWith('jrt='));
              }
              
              const { data } = await api.post("/auth/refresh_token");
              if (data && data.token) {
                refreshFailed.current = false; // Garantir que está resetado
                localStorage.setItem("token", JSON.stringify(data.token));
                api.defaults.headers.Authorization = `Bearer ${data.token}`;
                setIsAuth(true);
                setUser(data.user || data);
                return data;
              }
              throw new Error("No token in response");
            } catch (err) {
              // Se o refresh_token falhar, marcar como falho e limpar tudo
              refreshFailed.current = true;
              localStorage.removeItem("token");
              api.defaults.headers.Authorization = undefined;
              setIsAuth(false);
              setUser({});
              // Redirecionar para login se não estiver já lá
              if (history.location.pathname !== "/login" && !isPublicAccessPath()) {
                history.push("/login");
              }
              // Não mostrar erro se o token simplesmente expirou
              if (err?.response?.status !== 401 && err?.response?.status !== 403) {
                toastError(err);
              }
              throw err;
            } finally {
              isRefreshing.current = false;
              refreshPromise.current = null;
            }
          })();
          
          // Aguardar o refresh completar
          try {
            await refreshPromise.current;
          } catch (err) {
            // Erro já foi tratado na promessa
          }
        } else {
        }
      } else {
        // Se não há token, garantir que está deslogado
        refreshFailed.current = false; // Resetar flag quando não há token
        setIsAuth(false);
        setUser({});
        if (history.location.pathname !== "/login" && !isPublicAccessPath()) {
          history.replace("/login");
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      // Não fazer nada se ainda está carregando ou se refresh falhou
      if (loading || refreshFailed.current) {
        return;
      }
      
      // Verificar se há token antes de fazer a requisição
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }
      
      // Aguardar se está fazendo refresh
      if (isRefreshing.current && refreshPromise.current) {
        try {
          await refreshPromise.current;
          if (refreshFailed.current) {
            return;
          }
        } catch (err) {
          return;
        }
      }
      
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        // Se receber 401 ou 403, limpar tudo
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          clearAuthSession(true);
          refreshFailed.current = true;
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          setUser({});
        }
      }
    };
    
    if (isAuth && !refreshFailed.current && !loading) {
      fetchCurrentUser();
    }
  }, [isAuth, loading]);

  const handleLogin = async (userData) => {
    setLoading(true);
    // Resetar flag de falha antes do login
    refreshFailed.current = false;
    isRefreshing.current = false;
    refreshPromise.current = null;

    try {
      const { data } = await api.post("/auth/login", userData);
      
      // Verificar se o cookie foi recebido
      const cookies = document.cookie;
      const hasJrtCookie = cookies.includes('jrt=');
      
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting?.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        // Resetar flag de falha no login bem-sucedido
        refreshFailed.current = false;
        isRefreshing.current = false;
        refreshPromise.current = null;
        
        // Marcar timestamp do login para evitar refresh imediato
        window.__lastLoginTime = Date.now();
        
        // Salvar token ANTES de setar isAuth e user
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        
        // Aguardar um pouco para garantir que o token está salvo antes de fazer outras requisições
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setUser(data.user || data);
        const companyId = data.user?.companyId ?? data?.companyId;
        if (companyId != null) localStorage.setItem("lastLoginCompanyId", String(companyId));
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        // Aguardar mais um pouco antes de redirecionar para garantir que tudo está pronto
        await new Promise(resolve => setTimeout(resolve, 200));
        history.push("/tickets");
        setLoading(false);
      } else {
        // Resetar flags mesmo para empresa vencida
        refreshFailed.current = false;
        isRefreshing.current = false;
        refreshPromise.current = null;
        
        localStorage.setItem("token", JSON.stringify(data.token));
        const companyIdVenc = data.user?.companyId ?? data?.companyId;
        if (companyIdVenc != null) localStorage.setItem("lastLoginCompanyId", String(companyIdVenc));
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      refreshFailed.current = true;
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
